import { SettingSidebarProps } from '#components/ui/SettingSidebar/SettingSidebar';
import { useIsMutating } from '@tanstack/react-query';
import { useGetFiltersTree } from '@vindr/services/actions/filterManagement.action';
import { queryKey } from '@vindr/services/constants/queryClientKey';
import keycloak from '@vindr/services/keycloak';
import { SidePanel } from '@vindr/ui';
import Menu, { MenuProps } from '@vindr/ui/src/components/ui/Menu';
import isEmpty from 'lodash/isEmpty';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const OrderWorklistSidebar = ({
  collapsed,
  onChange,
  onSearch,
}: {
  collapsed: boolean;
  onChange: (val: boolean) => void;
  onSearch: (val: string, type?: 'filter_id' | 'institution_id' | 'all') => unknown;
}) => {
  const { t } = useTranslation('Vindoc');
  const { data: folders } = useGetFiltersTree();
  const [defaultInstitutionID, setDefaultInstitutionID] = useState(
    keycloak.userInfo?.default_institution_id
  );
  const isUpdatingProfile = useIsMutating({ mutationKey: [queryKey.update_profile] });

  const items: SettingSidebarProps['items'] = useMemo(() => {
    if (isEmpty(folders?.data)) return [];

    return folders?.data.map(
      (institution) =>
        ({
          label: institution.institution_name,
          key: institution.institution_id,
          type: 'institution_id',
          title: institution.institution_name,
          children: formatSidebarItems(institution.filters),
        } satisfies MenuProps['items'][number])
    );
  }, [folders]);

  const allKeys = useMemo(() => {
    const keys = [];

    items.forEach((item) => {
      keys.push(item.key);
      if (item.children) {
        item.children.forEach((child) => {
          keys.push(child.key);
        });
      }
    });

    return keys;
  }, [items]);

  return (
    allKeys && (
      <SidePanel width={220} collapsed={collapsed} onChange={onChange}>
        <Menu
          key={isUpdatingProfile}
          defaultOpenKeys={allKeys}
          defaultSelected={defaultInstitutionID || ''}
          onClick={({ key, type }) => {
            // Not search when have space in key
            if (/ /.test(key) || !type) return;

            if (window.innerWidth < 640) onChange?.(true);
            onSearch(key, type);
            setDefaultInstitutionID(key);
          }}
          items={[
            {
              label: t('All'),
              key: '',
              type: 'all',
              title: t('All'),
            } satisfies MenuProps['items'][number],
            ...items,
          ]}
        />
      </SidePanel>
    )
  );
};

function formatSidebarItems(folders) {
  return folders.map((folder) => {
    const obj: any = { key: folder.ID, label: folder.Name, title: folder.Name, type: 'filter_id' };

    if (folder.Children) {
      obj.children = formatSidebarItems(folder.Children);
    }

    return obj;
  });
}

export default memo(OrderWorklistSidebar, (prev, next) => {
  return prev.collapsed === next.collapsed;
});
